import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { RdsProgressSpinnerModule } from '@rds/angular-components';
import { SearchInputComponent } from '../../shared/search-input/search-input.component';
import { PromptChipsComponent } from '../../shared/prompt-chips/prompt-chips.component';
import { SparkleIconComponent } from '../../shared/sparkle-icon/sparkle-icon.component';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { SearchNavigationService } from '../../../services/search-navigation.service';
import { ASSISTANT_PROMPTS } from '../../../constants/assistant.constants';

/**
 * The homepage hero: one "Ask Panorama" field plus starter prompts.
 *
 * Both the field and the chips run a catalog search through
 * `SearchNavigationService`, which sends queries the catalog can answer to the
 * results page and the rest straight to the assistant. This component needs no
 * assistant feature flag; it inherits the search flag from
 * `inexp-search-input`.
 *
 * Kept separate from `HeroComponent`, which stays registered in `components.ts`
 * as the Storyblok `hero` block and is still reachable from a CMS story.
 */
@Component({
  selector: 'inexp-assistant-hero',
  imports: [
    SearchInputComponent,
    PromptChipsComponent,
    SparkleIconComponent,
    RdsProgressSpinnerModule,
  ],
  templateUrl: './assistant-hero.component.html',
  styleUrl: './assistant-hero.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantHeroComponent {
  private readonly _searchNavigation = inject(SearchNavigationService);
  private readonly _featureFlagService = inject(FeatureFlagService);

  /** True while the catalog is being probed, before we know where to go. */
  readonly isResolving = this._searchNavigation.isResolving;

  readonly title = input<string>('');
  readonly description = input<string>('');
  readonly searchPlaceholder = input<string>('');
  readonly searchButtonText = input<string>('');

  readonly prompts = ASSISTANT_PROMPTS;

  readonly isSearchEnabled = this._featureFlagService.createFlagSignal(
    'panorama_inexp_1741_search',
  );

  /**
   * Run a catalog search for `query`. Where it lands depends on whether the
   * catalog has anything to show; a blank one opens the chat with nothing
   * asked. The hero makes none of those calls itself — keeping the routing
   * policy in one service is why it has no judgement of its own here.
   */
  search(query: string): void {
    this._searchNavigation.submitQuery(query);
  }
}
